import { filter, chunk } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect, View } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import backFill from '@iconify/icons-eva/arrow-ios-back-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Modal,
  Box,
  Backdrop,
  Paper
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import {
  StorageListHead,
  StorageListToolbar,
  StorageMoreMenu
} from '../../components/replenishergroup/storage';
import StorageModal from './ReplenisherGroupStorageModal';
import ConfDialog from '../../components/ConfDialog';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'code', label: '保管庫コード', alignRight: false },
  { id: 'name', label: '保管庫名', alignRight: false },
  { id: '' }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] !== undefined ? a[orderBy] : a.with_storage[orderBy];
  const bVal = b[orderBy] !== undefined ? b[orderBy] : b.with_storage[orderBy];
  if (bVal < aVal) {
    return -1;
  }
  if (bVal > aVal) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, role) {
  if (query) {
    array = filter(
      array,
      (_storage) => _storage.with_storage.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  if (role !== -1) {
    array = filter(array, (_target) => _target.role === role);
  }

  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function ReplenisherGroupStorage({
  open = false,
  replenisher_groupId = 0,
  replenisher_groupCode = 0,
  replenisher_groupName = '',
  replenisherCode = 0,
  replenisherName = '',
  onClose = null,
  updateReplenisherGroupDt
}) {
  const navigate = useNavigate();

  const { SystemLib } = window;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [modal, setModal] = useState(false);
  const [storages, setStorages] = useState([]);
  const [currentTargetId, setCurrentTargetId] = useState(0);
  const [updateDt, setUpdateDt] = useState(moment().unix());

  const [isLoading, setIsLoading] = useState(false);
  const [conf, setConf] = useState(false);
  const [confDelAll, setConfDelAll] = useState(false);

  const [role, setRole] = useState(-1);

  const [isModal, setIsModal] = useState(true);
  const [groupName, setGroupName] = useState('');

  // useEffect(() => {
  //   getStorages();
  // }, []);

  useEffect(() => {
    if (onClose === null) {
      const { replenisher_name: gn } = SystemLib.userInfo;
      setGroupName(gn);
      setIsModal(false);
    } else {
      setGroupName(replenisherName);
    }
  }, []);

  useEffect(() => {
    getStorages();
  }, [replenisherCode, replenisher_groupCode, updateReplenisherGroupDt]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredStorages.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - storages.length) : 0;

  const filteredStorages = applySortFilter(
    storages,
    getComparator(order, orderBy),
    filterName,
    role
  );

  const isStorageNotFound = filteredStorages.length === 0;

  const useStyles = makeStyles({
    tableCell: {
      height: 10
    }
  });

  const getStorages = async () => {
    setIsLoading(true);
    const { data } = await SystemLib.api(
      'replenisher_group/storage/list',
      {
        replenisher_code: replenisherCode,
        replenisher_group_code: replenisher_groupCode
      },
      'post'
    );
    if (data.result) {
      setStorages(data.data);
    }
    setIsLoading(false);
  };

  const getStorage = async (id) => {
    const { data } = await SystemLib.api(`replenisher_group/storage/${id}`, {}, 'get');
    if (data.result) {
      let flag = false;
      if (storages) {
        const arr = storages.slice();
        arr.forEach((v, i) => {
          if (v.id === id) {
            arr[i] = data.data;
            setStorages(arr);
            flag = true;
          }
        });
      }
      if (!flag) {
        setOrderBy(null);
        const arr = storages.slice();
        arr.unshift(data.data);
        setStorages(arr);
      }
    }
  };

  const deleteStorages = async (ids) => {
    const tmp = chunk(ids, 1000);
    const p = [];
    tmp.forEach(async (arr) => {
      console.log(arr);
      p.push(
        SystemLib.api(
          `replenisher_group/storage/delete`,
          {
            ids: arr
          },
          'post'
        )
      );
    });
    await Promise.all(p);
    const newStorages = storages.filter((v) => !ids.includes(v.id));
    setStorages(newStorages);
  };

  const reload = async (id) => {
    getStorage(id);
  };

  const classes = useStyles();

  const checkRole = () => {
    const { role } = SystemLib.userInfo;
    if (role === 'admin' || role === 'master') {
      return true;
    }
    return false;
  };

  let title = '';
  if (replenisherName) {
    title = `${replenisherName}：`;
  }
  if (replenisher_groupName) {
    title += `${replenisher_groupName}：`;
  }
  title += '保管庫';
  if (checkRole()) {
    title += '管理';
  } else {
    title += '一覧';
  }

  return (
    <div
      style={
        isModal
          ? {
              position: 'absolute',
              top: 0,
              left: 0,
              background: '#fff',
              height: '100%',
              width: '100%',
              display: open ? 'block' : 'none'
            }
          : { flex: 1 }
      }
    >
      <Page title={title}>
        <Container sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" gutterBottom display="block">
              {title}
            </Typography>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Icon icon={backFill} />}
                onClick={() => {
                  if (onClose) {
                    onClose(replenisher_groupId);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                戻る
              </Button>
              {checkRole() && (
                <Button
                  variant="contained"
                  startIcon={<Icon icon={plusFill} />}
                  onClick={() => {
                    setCurrentTargetId(0);
                    setUpdateDt(moment().unix());
                    setModal(true);
                  }}
                >
                  新規登録
                </Button>
              )}
            </Stack>
          </Stack>

          <Card>
            <StorageListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              onDelete={() => {
                setConfDelAll(true);
              }}
              onChangeRole={(v) => {
                setRole(v);
              }}
              useMaster={false}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, maxHeight: '80vh' }}>
                <Table stickyHeader>
                  <StorageListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredStorages.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredStorages
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const {
                          id,
                          with_storage: { name, code }
                        } = row;
                        const isItemSelected = selected.indexOf(id) !== -1;
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox" height={50}>
                              {checkRole() && (
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={(event) => handleClick(event, id)}
                                />
                              )}
                            </TableCell>
                            <TableCell component="th" scope="row" padding="none" width={200}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" noWrap>
                                  {code}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                              sx={{ minWidth: 200 }}
                            >
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" noWrap>
                                  {name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" padding="none">
                              {checkRole() && (
                                <StorageMoreMenu
                                  itemId={id}
                                  onDelete={(id) => {
                                    setCurrentTargetId(id);
                                    setConf(true);
                                  }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isStorageNotFound && !isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                  {isStorageNotFound && isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <Paper>
                            <Typography gutterBottom align="center" variant="subtitle1">
                              読み込み中
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={filteredStorages.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="表示件数"
            />
          </Card>
        </Container>
        {modal && (
          <StorageModal
            storages={storages}
            replenisherCode={replenisherCode}
            replenisher_groupCode={replenisher_groupCode}
            targetId={currentTargetId}
            open={modal}
            onClose={() => {
              getStorages();
              setModal(false);
            }}
            updateDt={updateDt}
            reload={(id) => {
              reload(id);
            }}
          />
        )}
        <ConfDialog
          open={conf}
          onOk={() => {
            deleteStorages([currentTargetId]);
            setCurrentTargetId(0);
            setConf(false);
          }}
          onCancel={() => {
            setConf(false);
          }}
          onClose={() => {
            setConf(false);
          }}
        >
          削除してもよろしいでしょうか？
        </ConfDialog>
        <ConfDialog
          open={confDelAll}
          onOk={() => {
            deleteStorages(selected);
            setSelected([]);
            setConfDelAll(false);
          }}
          onCancel={() => {
            setConfDelAll(false);
          }}
          onClose={() => {
            setConfDelAll(false);
          }}
        >
          削除してもよろしいでしょうか？ ({selected.length}件)
        </ConfDialog>
      </Page>
    </div>
  );
}
